import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { BillingCycle, ProductSKU } from '../api/client'

// One line per SKU: quantity carries "how many", so adding the same product
// twice bumps the quantity instead of creating a duplicate row. The mail
// add-on caps at 1 (account-level) which the catalog's max_quantity enforces.
export interface CartLine {
  sku: ProductSKU
  quantity: number
  // Instance-scoped SKUs (mail) need a target instance, picked on the cart
  // page. Undefined until the buyer chooses one; checkout is blocked meanwhile.
  instanceId?: string
}

interface CartState {
  lines: CartLine[]
  // Billing cycle applies to the whole basket — a single charge can't mix a
  // monthly and a yearly renewal date.
  cycle: BillingCycle
  add: (sku: ProductSKU, quantity?: number, max?: number) => void
  setQuantity: (sku: ProductSKU, quantity: number, max?: number) => void
  setInstance: (sku: ProductSKU, instanceId: string) => void
  remove: (sku: ProductSKU) => void
  clear: () => void
  setCycle: (cycle: BillingCycle) => void
}

const clamp = (n: number, max: number) => Math.max(1, Math.min(n, max))

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      lines: [],
      cycle: 'monthly',

      add: (sku, quantity = 1, max = 50) =>
        set((s) => {
          const existing = s.lines.find((l) => l.sku === sku)
          if (existing) {
            return {
              lines: s.lines.map((l) =>
                l.sku === sku ? { ...l, quantity: clamp(l.quantity + quantity, max) } : l,
              ),
            }
          }
          return { lines: [...s.lines, { sku, quantity: clamp(quantity, max) }] }
        }),

      setQuantity: (sku, quantity, max = 50) =>
        set((s) => ({
          lines: s.lines.map((l) => (l.sku === sku ? { ...l, quantity: clamp(quantity, max) } : l)),
        })),

      setInstance: (sku, instanceId) =>
        set((s) => ({
          lines: s.lines.map((l) => (l.sku === sku ? { ...l, instanceId } : l)),
        })),

      remove: (sku) => set((s) => ({ lines: s.lines.filter((l) => l.sku !== sku) })),

      clear: () => set({ lines: [] }),

      setCycle: (cycle) => set({ cycle }),
    }),
    { name: 'oaas_cart' },
  ),
)

// Total item count, for the header badge.
export function useCartCount(): number {
  return useCartStore((s) => s.lines.reduce((n, l) => n + l.quantity, 0))
}
